import { createContext, useContext, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Socket } from "socket.io-client";

import { createAuthedSocket } from "@/utils/createAuthedSocket";

import { useAppDispatch } from "@/redux/hooks";
import { useIsFeatureActive } from "@/utils/useIsFeatureActive";
import { FeaturesCodes } from "@/pages/Private/pages/Users/enum/codes";
import { authSelector } from "@/pages/Public/redux/auth.slice";

import {
	addRealtimeNotification,
	setUnreadCount,
} from "@/pages/Private/redux/notification/notification.slice";

import { Notification } from "../schema/notification";

// ── Socket event names ─────────────────────────────────────────────────
enum NotificationSocketEvent {
	JOIN_USER_ROOM = "notification-join-user-room",
	NEW = "notification-new",
	BADGE_UPDATE = "notification-badge-update",
	ERROR = "notification-error",
}

// ── Context ────────────────────────────────────────────────────────────
interface NotificationSocketContextType {
	socket: Socket | null;
	latestNotification: Notification | null;
}

const NotificationSocketContext = createContext<NotificationSocketContextType | undefined>(
	undefined
);

// ── Provider ───────────────────────────────────────────────────────────
export const NotificationSocketProvider = ({ children }: { children: React.ReactNode }) => {
	const [socket, setSocket] = useState<Socket | null>(null);
	const [latestNotification, setLatestNotification] = useState<Notification | null>(null);
	const dispatch = useAppDispatch();
	const auth = useSelector(authSelector);
	const token = auth?.token;
	const tokenRef = useRef(token);

	tokenRef.current = token;
	const userId = auth?.user?.id;
	const isActive = useIsFeatureActive(FeaturesCodes.NOTIFICATION_CENTER);

	// Ref to avoid stale closure issues in socket event handlers
	const dispatchRef = useRef(dispatch);

	dispatchRef.current = dispatch;

	useEffect(() => {
		if (!token || !userId || !isActive) {
			return;
		}

		const newSocket = createAuthedSocket(import.meta.env.VITE_API_URL, () => tokenRef.current);

		let hasLoggedError = false;

		newSocket.on("connect", () => {
			hasLoggedError = false;
			console.info("[NotificationSocket] Connected");
			newSocket.emit(NotificationSocketEvent.JOIN_USER_ROOM, String(userId));
		});

		newSocket.on("connect_error", (error) => {
			if (!hasLoggedError) {
				console.warn("[NotificationSocket] Connection error:", error);
				hasLoggedError = true;
			}
		});

		newSocket.on(NotificationSocketEvent.NEW, (notification: Notification) => {
			dispatchRef.current(addRealtimeNotification(notification));
			setLatestNotification(notification);
		});

		newSocket.on(
			NotificationSocketEvent.BADGE_UPDATE,
			({ unreadCount }: { unreadCount: number }) => {
				dispatchRef.current(setUnreadCount(unreadCount));
			}
		);

		newSocket.on(NotificationSocketEvent.ERROR, (message: string) => {
			console.error("[NotificationSocket] Error:", message);
		});

		newSocket.on("disconnect", (reason) => {
			console.warn("[NotificationSocket] Disconnected:", reason);
		});

		setSocket(newSocket);

		return () => {
			newSocket.disconnect();
		};
	}, [token, userId, isActive]);

	return (
		<NotificationSocketContext.Provider value={{ socket, latestNotification }}>
			{children}
		</NotificationSocketContext.Provider>
	);
};

// ── Hook ───────────────────────────────────────────────────────────────
export const useNotificationSocket = () => {
	const context = useContext(NotificationSocketContext);

	if (!context) {
		throw new Error("useNotificationSocket must be used within a NotificationSocketProvider");
	}

	return context;
};
